import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { useState } from "react";
import type { FormEvent } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  fetchNeighborhoodStats,
  Mls2ApiError,
  type Distribution,
  type NeighborhoodStats,
} from "../lib/api";

ChartJS.register(
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
);

const percentileLabels = ["Minimum", "25th percentile", "Median", "75th percentile", "Maximum"];

const lineOptions: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: "index" },
  plugins: {
    legend: { display: false },
    tooltip: { displayColors: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, grid: { color: "#e2e8f0" } },
  },
};

const barOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { displayColors: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      ticks: { precision: 0 },
      grid: { color: "#e2e8f0" },
    },
  },
};

export function Analytics() {
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [stats, setStats] = useState<NeighborhoodStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchNeighborhoodStats(
        city.trim(),
        neighborhood.trim(),
      );
      setStats(response);
    } catch (requestError) {
      setStats(null);
      setError(
        requestError instanceof Mls2ApiError
          ? requestError.message
          : "Unable to load neighborhood analytics.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="mx-auto max-w-7xl">
      <header>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
          Market intelligence
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
          Neighborhood analytics
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Explore canonical listing volume, room mix, pricing ranges, and
          time-on-market estimates for a selected neighborhood.
        </p>
      </header>

      <form
        className="mt-8 grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-[1fr_1fr_auto]"
        onSubmit={handleSubmit}
      >
        <label className="text-sm font-medium text-slate-700">
          City
          <input
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
            type="text"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            placeholder="Miami"
            required
          />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Neighborhood
          <input
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
            type="text"
            value={neighborhood}
            onChange={(event) => setNeighborhood(event.target.value)}
            placeholder="Brickell"
            required
          />
        </label>
        <button
          className="self-end rounded-lg bg-mls-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:cursor-not-allowed disabled:opacity-60"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "Loading…" : "Run analysis"}
        </button>
      </form>

      {error ? (
        <p
          className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      {isLoading ? <AnalyticsSkeleton /> : null}

      {!isLoading && stats ? <AnalyticsResults stats={stats} /> : null}

      {!isLoading && !stats && !error ? (
        <div className="mt-6 grid min-h-72 place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <div>
            <p className="font-semibold text-slate-900">Choose a market to begin</p>
            <p className="mt-2 text-sm text-slate-500">
              Enter a city and neighborhood to retrieve live MLS2 statistics.
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function AnalyticsResults({ stats }: { stats: NeighborhoodStats }) {
  const roomEntries = Object.entries(stats.roomCountDistribution).sort(
    ([left], [right]) => Number(left) - Number(right),
  );

  return (
    <div className="mt-6 space-y-6" aria-live="polite">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Median price"
          value={formatCurrency(stats.medianPrice)}
          detail={marketName(stats)}
        />
        <MetricCard
          label="Listing volume"
          value={stats.listingVolume.toLocaleString()}
          detail="Canonical active records"
        />
        <MetricCard
          label="Median price / m²"
          value={formatCurrency(stats.pricePerSqmDistribution.median)}
          detail="Normalized by floor area"
        />
        <MetricCard
          label="Median market time"
          value={formatDays(stats.timeOnMarketEstimatesDays.median)}
          detail="Estimated from observed dates"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ChartCard
          title="Price per square meter"
          description="Distribution across the selected market."
        >
          <Line
            options={lineOptions}
            data={distributionLineData(
              stats.pricePerSqmDistribution,
              "Price / m²",
              "#21624b",
              "rgba(33, 98, 75, 0.14)",
            )}
          />
        </ChartCard>

        <ChartCard
          title="Room distribution"
          description="Listing volume grouped by room count."
        >
          {roomEntries.length ? (
            <Bar
              options={barOptions}
              data={{
                labels: roomEntries.map(([rooms]) => rooms + " rooms"),
                datasets: [
                  {
                    label: "Listings",
                    data: roomEntries.map(([, count]) => count),
                    backgroundColor: "#174c3b",
                    borderRadius: 7,
                  },
                ],
              }}
            />
          ) : (
            <ChartEmpty />
          )}
        </ChartCard>

        <ChartCard
          title="Time on market trend"
          description="Observed range from first seen to last update."
        >
          <Line
            options={lineOptions}
            data={distributionLineData(
              stats.timeOnMarketEstimatesDays,
              "Days",
              "#dd6c3c",
              "rgba(221, 108, 60, 0.14)",
            )}
          />
        </ChartCard>

        <DistributionCard
          title="Price-per-square-meter distribution"
          distribution={stats.pricePerSqmDistribution}
          formatter={formatCurrency}
        />
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
        {value}
      </p>
      <p className="mt-2 text-xs text-slate-500">{detail}</p>
    </article>
  );
}

function ChartCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="font-semibold text-slate-950">{title}</h2>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
      <div className="mt-6 h-72">{children}</div>
    </article>
  );
}

function DistributionCard({
  title,
  distribution,
  formatter,
}: {
  title: string;
  distribution: Distribution;
  formatter: (value: number | null) => string;
}) {
  const values = distributionValues(distribution);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="font-semibold text-slate-950">{title}</h2>
      <p className="mt-1 text-xs text-slate-500">
        Five-number summary for the selected neighborhood.
      </p>
      <dl className="mt-6 divide-y divide-slate-100">
        {percentileLabels.map((label, index) => (
          <div className="flex items-center justify-between py-3" key={label}>
            <dt className="text-sm text-slate-600">{label}</dt>
            <dd className="font-semibold text-slate-950">
              {formatter(values[index])}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="mt-6 animate-pulse space-y-6" aria-label="Loading analytics">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div className="h-32 rounded-2xl bg-white" key={index} />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="h-96 rounded-2xl bg-white" />
        <div className="h-96 rounded-2xl bg-white" />
      </div>
    </div>
  );
}

function ChartEmpty() {
  return (
    <div className="grid h-full place-items-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500">
      No chart data available.
    </div>
  );
}

function distributionLineData(
  distribution: Distribution,
  label: string,
  color: string,
  fillColor: string,
) {
  return {
    labels: percentileLabels,
    datasets: [
      {
        label,
        data: distributionValues(distribution),
        borderColor: color,
        backgroundColor: fillColor,
        fill: true,
        tension: 0.35,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };
}

function distributionValues(distribution: Distribution): Array<number | null> {
  return [
    distribution.min,
    distribution.p25,
    distribution.median,
    distribution.p75,
    distribution.max,
  ];
}

function marketName(stats: NeighborhoodStats): string {
  return [stats.neighborhood, stats.city].filter(Boolean).join(", ") || "All markets";
}

function formatCurrency(value: number | null): string {
  return value === null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
}

function formatDays(value: number | null): string {
  return value === null ? "—" : Math.round(value).toLocaleString() + " days";
}